import { componentText, HostAriaLabelMixin, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { takeUntil, timer } from 'rxjs'

/** Fired on an unmodified primary activation — a plain left click, or Enter
 *  (Space too, on the button form). Cancel it to keep the browser from
 *  following `href`. */
export type SchmancyLinkPressEvent = CustomEvent<void>

/** The floor WCAG 2.2 SC 2.5.8 Target Size (Minimum) sets, in CSS pixels. */
const MIN_TARGET = 24

// DEV-only conformance guard. Every allocation is behind `import.meta.env.DEV`
// so production ships none of it — the same idiom as overflowWithin()'s
// structural guard and state/active-host.ts's dev slot.
const warned: WeakSet<Element> | undefined = import.meta.env.DEV ? new WeakSet<Element>() : undefined

/**
 * Whether the run has non-target text beside it in its own parent — the
 * composition SC 2.5.8's Inline exception names ("the target is in a sentence
 * or its size is otherwise constrained by the line-height of non-target text").
 *
 * Read from the LIGHT-DOM parent rather than the flattened slot parent: the
 * primitives a run of prose is written into (schmancy-typography above all)
 * render a bare slot, so the text nodes that share the run's line box are its
 * own siblings, and the element next to the slot inside that component's
 * shadow root is not where any of them live.
 */
function sitsInText(host: HTMLElement): boolean {
	const parent = host.parentNode
	if (!parent) return false
	for (const node of Array.from(parent.childNodes)) {
		if (node === host) continue
		if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true
	}
	return false
}

/**
 * The first other target whose box the undersized run's 24px-diameter spacing
 * circle reaches, or null when the run has the clearance SC 2.5.8's Spacing
 * exception asks for.
 *
 * Bounded to the run's own root: a link in a fine-print block sits with its
 * siblings in one shadow root, which is the crowding this can see and the
 * crowding that actually happens. A target in a different root — a button the
 * consumer's parent painted underneath — is outside what one element can
 * measure, so the guard understates and never invents.
 */
function crowdingTarget(host: HTMLElement, rect: DOMRect): Element | null {
	const root = host.getRootNode() as Document | ShadowRoot
	const cx = rect.left + rect.width / 2
	const cy = rect.top + rect.height / 2
	const radius = MIN_TARGET / 2
	for (const other of Array.from(
		root.querySelectorAll('a[href], button, schmancy-link, schmancy-button, [role="link"], [role="button"]'),
	)) {
		if (other === host || host.contains(other) || other.contains(host)) continue
		// A component's own target is the element carrying the role, which is one
		// shadow root in; the host around it is a roleless box that can be wider
		// or narrower than what a pointer actually hits.
		const box = (other.shadowRoot?.querySelector('[part="base"]') ?? other).getBoundingClientRect()
		if (box.width === 0 && box.height === 0) continue
		const dx = Math.max(box.left - cx, 0, cx - box.right)
		const dy = Math.max(box.top - cy, 0, cy - box.bottom)
		if (dx * dx + dy * dy < radius * radius) return other
	}
	return null
}

/**
 * Warns once when the composition a run was placed in contradicts what the run
 * assumes about it. Two criteria, one measurement — whether prose sits beside
 * the run — because both of them turn on exactly that:
 *
 * - `standalone` drops the resting rule, and only a run with no prose beside it
 *   can afford to. Inside a sentence it leaves the link and the text around it
 *   identical at rest, with no colour of its own to tell them apart, which is
 *   SC 1.4.1 with nothing left to fall back on.
 * - Without prose beside it the run cannot claim SC 2.5.8's Inline exception,
 *   so Spacing has to reach it instead, and an undersized target that another
 *   target crowds has neither.
 *
 * Both are properties of the composition, not of the control, so the running
 * element is the only thing in the system that can tell whether either holds:
 * the criteria are answered by measurement here rather than by an assumption
 * written into the primitive.
 */
function devWarnComposition(host: HTMLElement, base: HTMLElement | null, standalone: boolean): void {
	if (!import.meta.env.DEV || !base || warned!.has(host)) return
	if (sitsInText(host)) {
		if (!standalone) return
		warned!.add(host)
		console.warn(
			`[schmancy] <schmancy-link standalone> sits in a sentence. \`standalone\` drops the run's resting` +
				` underline, which only a run with no prose beside it can afford: inside prose it leaves the link and` +
				` the text around it identical at rest, and the run carries no colour of its own to tell the two` +
				` apart. That is WCAG 2.2 SC 1.4.1 with nothing left to fall back on. Drop \`standalone\` here — it is` +
				` for a run that stands on its own, where there is no prose for a reader to confuse it with.`,
			host,
		)
		return
	}
	const rect = base.getBoundingClientRect()
	if (rect.height === 0) return
	if (rect.height >= MIN_TARGET && rect.width >= MIN_TARGET) return
	const crowding = crowdingTarget(host, rect)
	if (!crowding) return
	warned!.add(host)
	console.warn(
		`[schmancy] <schmancy-link> is a ${Math.round(rect.width)}x${Math.round(rect.height)} target with` +
			` <${crowding.tagName.toLowerCase()}> inside its ${MIN_TARGET}px spacing circle, and it has no` +
			` non-target text beside it. That is WCAG 2.2 SC 2.5.8 with neither the Inline nor the Spacing` +
			` exception available: a pointer landing between the two is as likely to reach the wrong one.` +
			` Padding the run out is not the fix — vertical padding on an inline box costs no layout but` +
			` overlaps the neighbour, and an overlapping area is subtracted from both targets. Move the two` +
			` apart until a ${MIN_TARGET}px circle centred on each clears the other, or put this run inside` +
			` the sentence it belongs to.`,
		host,
		crowding,
	)
}

/**
 * A pressable run of text with no box.
 *
 * It occupies exactly the line box of the text it sits in — no height of its
 * own, no padding on the block axis that layout can see, no minimum control
 * dimension — so putting one into a band of prose leaves that band's height
 * exactly where it was. That is the whole reason it is not a button: every rung
 * of the control ladder starts at 24px, and a control on the shortest rung
 * dropped into a 20px line of fine print grows the line by the difference,
 * once, at first paint, where no transition can hide it.
 *
 * ## What it renders
 *
 * An `<a href>` when `href` is set. A run that goes nowhere is not an anchor:
 * `href="#"` with a click handler puts a link role on a control that never
 * navigates, breaks middle-click and ⌘-click into a jump to the top of the
 * page, and offers the browser a destination it will happily follow if the
 * handler ever throws first.
 *
 * Without `href` it renders a span carrying `role="button"` and `tabindex="0"`,
 * and NOT a native `<button>`: every engine coerces a button away from
 * `display: inline` to `inline-block`, which makes it an atomic box — its
 * padding becomes real layout, and a label at the end of a line jumps whole to
 * the next line instead of breaking across it. Measured in the lab, where the
 * native-button form grew a 20px band to 24px in a flex row while the anchor
 * form left it at 20. The role brings its own obligation, which is discharged
 * here: Enter and Space both activate, and Space's page scroll is taken back.
 *
 * ## The resting mark
 *
 * A run inside prose owes a distinction from the prose around it, and in this
 * theme that distinction cannot be colour: primary ink can sit under the
 * 3:1 distinction from surrounding body or fine-print ink. The run therefore
 * keeps its resting rule even when its ink clears contrast against the ground.
 *
 * `standalone` is the case where that duty lapses: a run with no prose beside
 * it has nothing a reader could confuse it with, so the mark moves to approach —
 * the treatment `schmancy-breadcrumb` and `schmancy-button variant="plain"`
 * already carry. It is opt-in because the safe reading is the default, and a
 * DEV guard warns when it is claimed on a run that does sit in a sentence.
 *
 * `current` is the third state in that same vocabulary, and it outranks both:
 * current > resting > hover. The run whose destination is the one on screen
 * carries a solid 2px rule at the same offset, and the arm it outranks is
 * dropped rather than stacked — a run cannot wear two decorations, since
 * `text-decoration` is one property and the last arm to set it would silently
 * win. It is orthogonal to `standalone` and legitimate on either, because what
 * `standalone` decides is whether prose sits beside the run and what `current`
 * decides is whether the run points at the page already open.
 *
 * The mark is `decoration-2` against the 1px rule every other state renders,
 * which is the whole distinguishing axis and the reason it is thickness rather
 * than weight, size, or opacity: `text-decoration-thickness` does not enter the
 * line box, and the other three do. `link.test.ts` asserts the band height of a
 * current run against a bandless control, the same measurement the resting run
 * already carries.
 *
 * `aria-current="page"` rides on the role-carrying element — the `<a>` or the
 * `role="button"` span, one shadow root in — and is absent rather than `"false"`
 * when the run is not current, matching `schmancy-step`. A `current` run that
 * still has `href` still navigates: pointing at the page on screen is a
 * statement about which entry is marked, not a reason to take its address away.
 * That is where this differs from `schmancy-breadcrumb-item`, whose `current`
 * additionally suppresses the anchor because a trail's last crumb has nowhere
 * left to go; the name is the same because the concept is.
 *
 * ## Press versus navigate
 *
 * `press` fires only on an unmodified primary activation. ⌘, ctrl, shift, ⌥,
 * and every non-primary button are left to the browser, so "open in a new tab",
 * "open in a new window" and "download" keep working on a link whose in-page
 * activation opens an overlay instead of navigating. Cancel `press` to suppress
 * the navigation; leave it alone and the `href` is followed as well, which is
 * what a handler that only records the click wants.
 *
 * ## Target size
 *
 * The run is under 24px tall at every fine-print type scale, and it cannot be
 * anything else without becoming the box it exists not to be. WCAG 2.2 SC 2.5.8
 * has two exceptions that reach it, both properties of where it was placed:
 * Inline (in a sentence, or sized by the line-height of non-target text) and
 * Spacing (no other target within a 24px circle centred on it). A DEV-only
 * guard measures the rendered run against both and warns, once, with the
 * crowding element attached, when neither holds — which is the case the
 * primitive genuinely cannot fix from inside itself.
 *
 * @fires press - Unmodified primary activation. Cancelable; cancelling suppresses navigation.
 * @csspart base - The `<a>` (or the `role="button"` span) that carries the role.
 */
@schmancyElement('schmancy-link')
export class SchmancyLink extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				/* Inline, not inline-block or inline-flex: only an inline box wraps
				   with the sentence around it and stays out of line-box height. An
				   inline-block would size to its own content box plus padding and
				   push every line it lands in taller, which is the defect this
				   element exists to not have. */
				display: inline;
				color: inherit;

				/* Tailwind's preflight, inside the one sheet schmancy adopts into
				   every shadow root, declares line-height and font-family on :host
				   directly. A value SPECIFIED on :host outranks the one inherited
				   through it, so without this a run dropped into a 14px/20px line of
				   fine print renders at preflight's own 1.5 — 21px — and grows the
				   band by the difference, which is the single thing this element
				   exists not to do. link.test.ts asserts the band's height against the
				   same band without the run.

				   Local on purpose. The reset belongs library-wide — one
				   line-height: inherit beside the color-scheme: inherit already in
				   the schmancy.base :host block of mixins/tailwind.css, which is
				   there for this exact mechanism — because preflight strands EVERY
				   component at 1.5, not just this one. It is held because it repaints
				   every component nested in a context whose line-height is not 1.5,
				   an icon in a headline or a badge in display type, and nothing here
				   can see that: a green unit suite is not pixel-identity across
				   ninety components. It earns landing on a pixel-diff harness over
				   the lab's component matrix. */
				font: inherit;
			}
		`,
	]

	/** Renders as an anchor when set, and as a `role="button"` run when it is not. */
	@property() href?: string

	@property() target?: string

	/** Defaults to `noopener noreferrer` while `target="_blank"`. */
	@property() rel?: string

	/** Declares that no prose sits beside this run, which is the one case where
	 *  it owes no distinction from prose: the resting rule comes off and the mark
	 *  returns on hover. A run inside a sentence never sets it — see the class
	 *  doc for the contrast measurement that closes the alternative. */
	@property({ type: Boolean }) standalone = false

	/** Marks this run as the destination currently on screen: `aria-current="page"`
	 *  on the role-carrying element, and a solid 2px rule that outranks both the
	 *  resting and the hover mark. A `current` run with an `href` still navigates. */
	@property({ type: Boolean, reflect: true }) current = false

	/** Enter and Space are what a `button` role promises, and a span gets neither
	 *  for free. Routed through the element's own click so activation has one
	 *  path rather than two that can drift. */
	private activateKey = (e: KeyboardEvent) => {
		if (e.key !== 'Enter' && e.key !== ' ') return
		// Space scrolls the page on anything that is not a native button.
		e.preventDefault()
		;(e.currentTarget as HTMLElement).click()
	}

	private activate = (e: MouseEvent) => {
		// A modified or non-primary click already belongs to the browser: ⌘ and
		// ctrl open a tab, shift a window, ⌥ downloads, the middle button opens in
		// the background. Intercepting any of them takes away a navigation the
		// user asked for explicitly, by holding a key down.
		if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
		const press = new CustomEvent('press', { bubbles: true, composed: true, cancelable: true })
		this.dispatchEvent(press)
		if (press.defaultPrevented) e.preventDefault()
	}

	protected override firstUpdated(): void {
		if (!import.meta.env.DEV) return
		// One frame on: the run has to be laid out into its line before its target
		// can be measured, and the guard's whole subject is that measurement.
		timer(0)
			.pipe(takeUntil(this.disconnecting))
			.subscribe(() => devWarnComposition(this, this.renderRoot.querySelector('[part="base"]'), this.standalone))
	}

	override render() {
		const external = this.target === '_blank'
		const accessibleLabel = this.ariaLabel
			? `${this.ariaLabel}${external ? ` ${componentText('link.newWindow')}` : ''}`
			: undefined
		const classes = this.classMap({
			// px-0 py-0.5: vertical padding on an inline box never enters line-box
			// height, so this is 4px of extra target the composition pays nothing
			// for. Half a step and no more, because past the line box the run would
			// reach into the line above and below, and an overlapping area is
			// subtracted from both targets rather than added to either.
			'inline bg-transparent text-inherit cursor-pointer px-0 py-0.5': true,
			// One token appears under exactly one key below. `this.classMap` splits
			// every multi-token key into its own entry before delegating to lit's,
			// so a token written under two keys is one object property assigned
			// twice and the later arm decides it outright — an `underline` shared by
			// a true arm and a false arm resolves to false and takes the resting
			// rule off a run that was never current.
			//
			// The offset is the value that clears the descenders of the prose scale
			// this run is set at, and it is unconditional because a rule the run is
			// not currently drawing has no offset to place.
			'underline-offset-4': true,
			// The rule at rest: the only affordance a run with no box has is its own
			// decoration, and colour cannot stand in for it here — the class doc
			// carries the measurement. `standalone` is the composition that owes no
			// distinction from prose; `current` outranks both and draws its own.
			underline: !this.standalone || this.current,
			// Dotted at rest, solid under the pointer — a hover state that costs no
			// layout to switch to.
			'decoration-dotted hover:decoration-solid': !this.standalone && !this.current,
			// Nothing beside it to be told apart from, so the mark moves to approach.
			'hover:underline': this.standalone && !this.current,
			// The destination already on screen: solid, twice the thickness of every
			// other state, and it replaces the resting rule rather than stacking on
			// it. Thickness rather than weight or size because it is the one axis of
			// the three that never reaches the line box.
			'decoration-2': this.current,
			'outline-secondary-default outline-hidden focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2': true,
		})
		// sr-only is absolutely positioned, so the announcement rides in the
		// accessible name and contributes nothing to the line box either.
		const label = html`<slot></slot>${when(
				external,
				() => html`<span class="sr-only"> ${componentText('link.newWindow')}</span>`,
			)}`
		// Not one whitespace text node may sit beside the role-carrying element in
		// this shadow root. It renders inside the host's own inline box, so an
		// indented template puts a space between the run and the punctuation the
		// sentence continues with — "unsere AGB ." — which is why the tags close
		// across the line break rather than the content doing it.
		return when(
			this.href,
			() =>
				html`<a
					part="base"
					class=${classes}
					href=${ifDefined(this.href)}
					target=${ifDefined(this.target)}
					rel=${ifDefined(this.rel ?? (external ? 'noopener noreferrer' : undefined))}
					aria-label=${ifDefined(accessibleLabel)}
					aria-current=${ifDefined(this.current ? 'page' : undefined)}
					@click=${this.activate}
					>${label}</a
				>`,
			() =>
				html`<span
					part="base"
					role="button"
					tabindex="0"
					class=${classes}
					aria-label=${ifDefined(accessibleLabel)}
					aria-current=${ifDefined(this.current ? 'page' : undefined)}
					@click=${this.activate}
					@keydown=${this.activateKey}
					>${label}</span
				>`,
		)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-link': SchmancyLink
	}
}
